import React, { useEffect, useContext, useCallback, useState } from 'react';

import { useField, ComparatorContext, KiziLogger } from '@kizi/comparator-core';

import { Button, Div, Heading, P } from '@kizi/core-components';

const navigateToAffiliate = (orderId: string, url) => {
  if (orderId) {
    location = url;
  }
};

interface AffiliateUrlModalProps {
  affiliateUrlClientId: string;
}

export const AffiliateUrlModal = React.memo(
  ({ affiliateUrlClientId }: AffiliateUrlModalProps) => {
    const selectedProductField = useField('overview.selectedProduct');
    if (!selectedProductField) return null;

    const { api } = useContext(ComparatorContext);
    const [orderIdResult, setOrderIdResult] = useState<any | null>({});

    const { commonTechnical, productCode, affiliateUrl } = JSON.parse(
      selectedProductField?.value as any,
    );

    useEffect(() => {
      (async () => {
        const result = await api.orderAffiliateContentPackage(
          commonTechnical[0].transactionReference,
          productCode,
        );

        setOrderIdResult(result);
      })();
    }, []);

    let affiliateProperUrl = affiliateUrl.replace(
      '%clientId%',
      affiliateUrlClientId,
    );

    if (
      Array.isArray(commonTechnical) &&
      orderIdResult.orderId &&
      commonTechnical.length > 0
    ) {
      affiliateProperUrl = affiliateProperUrl
        .replace('%referenceId%', commonTechnical[0].transactionReference)
        .replace('%orderId%', orderIdResult.orderId);
    }

    useEffect(() => {
      if (
        Array.isArray(commonTechnical) &&
        orderIdResult.orderId &&
        commonTechnical.length > 0
      ) {
        navigateToAffiliate(orderIdResult.orderId, affiliateProperUrl);
      } else {
        KiziLogger.logError({
          message: 'Could not open affiliate URL',
          eventId: 0,
          eventName: 'Unhandled error',
          member: api.credentials.clientId as string,
          data: {
            orderIdResult,
            commonTechnical,
            selectedProduct: JSON.parse(selectedProductField?.value as string),
          },
        });
      }
    }, [orderIdResult.orderId]);

    const onClick = useCallback(() => {
      navigateToAffiliate(orderIdResult.orderId, affiliateProperUrl);
    }, [orderIdResult.orderId]);

    return (
      <Div
        position="fixed"
        top={0}
        left={0}
        bottom={0}
        right={0}
        display="grid"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        bg="second.2"
        overflow="auto"
      >
        <Div
          display="flex"
          flexDirection="column"
          alignItems="center"
          gridGap={3}
          maxWidth={800}
          m={4}
          p={6}
          boxShadow="default"
          borderRadius={2}
          bg="white"
        >
          <svg
            width="61"
            height="60"
            viewBox="0 0 61 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M29.375 5.625C17.5885 5.625 8 15.3442 8 27.2914C8 39.2387 17.5885 48.9579 29.375 48.9579C31.224 48.9579 33.007 48.6951 34.7188 48.2456V38.7771C33.3152 42.8269 31.2863 45.3469 29.375 45.3469C27.0986 45.3469 24.6457 41.8061 23.2937 36.3191H34.7188V32.708H22.6083C22.3786 31.0126 22.25 29.1963 22.25 27.2914C22.25 25.3703 22.4134 23.5825 22.6466 21.8748H36.1034C36.3366 23.5825 36.5 25.3703 36.5 27.2914C36.5 27.7862 36.4673 28.2569 36.4513 28.7408C37.4042 27.8489 38.666 27.2914 40.0625 27.2914C40.0625 25.4027 39.9433 23.5977 39.739 21.8748H46.363C46.8954 23.5856 47.1875 25.4045 47.1875 27.2914C47.1875 28.5463 47.0471 29.7657 46.8048 30.9483L49.922 33.2018C50.4493 31.3204 50.75 29.3425 50.75 27.2914C50.75 15.3442 41.1616 5.625 29.375 5.625ZM29.375 9.23608C31.6458 9.23608 34.0679 12.8006 35.4215 18.2638H23.3285C24.6822 12.8006 27.1042 9.23608 29.375 9.23608ZM22.3613 10.6995C21.1787 12.7353 20.2382 15.3115 19.6025 18.2638H13.9596C15.8773 14.915 18.8247 12.2418 22.3613 10.6995ZM36.3887 10.6995C39.9253 12.2418 42.8727 14.915 44.7905 18.2638H39.1475C38.5118 15.3115 37.5713 12.7353 36.3887 10.6995ZM12.387 21.8748H19.0111C18.8067 23.5977 18.6875 25.4027 18.6875 27.2914C18.6875 29.18 18.808 30.9837 19.0111 32.708H12.3836C11.8528 30.9964 11.5625 29.1782 11.5625 27.2914C11.5625 25.4045 11.8546 23.5856 12.387 21.8748ZM40.0903 30.8814C39.578 30.8703 39.0858 31.0834 38.7395 31.4662C38.3931 31.849 38.2261 32.3644 38.2812 32.8808V48.9579C38.2812 49.9545 39.0793 50.7635 40.0625 50.7635C40.5755 50.7635 41.029 50.5372 41.3532 50.1851L41.3636 50.1887L44.714 46.1755L48.266 53.3766C48.5506 53.9537 49.1173 54.333 49.7527 54.3717C50.3881 54.4104 50.9955 54.1026 51.3462 53.5641C51.6969 53.0257 51.7376 52.3385 51.4528 51.7614L47.8277 44.4158L53.0218 43.4708L53.0253 43.439C53.7627 43.2206 54.3125 42.5555 54.3125 41.7358C54.3125 41.1056 53.9725 40.5778 53.488 40.2546L53.495 40.2194L41.3115 31.4103C40.9869 31.0811 40.5494 30.8916 40.0903 30.8814ZM13.9734 36.3191H19.599C20.2331 39.2766 21.1751 41.8467 22.3579 43.8833C18.8256 42.3414 15.8918 39.6666 13.9734 36.3191Z"
              fill="#004DF5"
            />
          </svg>
          <Heading as="h2" variant="H2" textAlign="center" my={0} mx={1}>
            Binnen enkele seconden sturen we je door naar de verzekeraar
          </Heading>
          <P variant="bodyRegular" color="second.0" textAlign="center">
            Verschijnt deze pagina niet binnen enkele seconden,{' '}
            <Button variant="transparent" onClick={onClick}>
              klik dan hier
            </Button>
            .
          </P>
        </Div>
      </Div>
    );
  },
);
